<script lang="ts" setup>
import { computed, ref, onMounted } from 'vue';
import { DataObjectType } from '@/gql/graphql';
import {
  BAlert,
  BBadge,
  BButton,
  BForm,
  BFormSelect,
  BFormCheckbox,
  BFormInput,
  BFormGroup,
  BModal,
  BPagination,
  BSpinner,
  BTable,
  BTooltip,
} from 'bootstrap-vue-next';
import Papa from 'papaparse';
import { formatFileSize, formatFileName } from '@/utils/file-utils';
import { formatDateTime } from '@/utils/date-utils';
import { getFileDownloadUrl } from '@/utils/fileService';
import {
  highlightJson,
  highlightLog,
  highlightPython,
  highlightShell,
} from '@/utils/prism-utils';
import { PAGING_ITEMS_PER_PAGE } from '@/constants';

// --- Component properties ---
const props = defineProps<{
  show: boolean;
  dataObject: DataObjectType;
  bucketName: string;
  bucketId: number;
  allowDownload: boolean;
}>();

// --- Component state ---
const downloadUrl = ref<string | null>(null);
const igvRemoteUrl = 'https://igv.org/app';
const igvLocalUrl = 'http://localhost:60151/load';
const igvUrl = ref(igvLocalUrl);
const igvUrlOptions = ref<Array<{ value: string; text: string }>>([
  { value: igvLocalUrl, text: 'IGV Desktop (recommended)' },
  { value: igvRemoteUrl, text: 'IGV Web (igv.org)' },
]);
const igvNoPhiConfirmed = ref<boolean>(false);
const igvGenome = ref('hg38');
const igvLocus = ref<string | null>('');
const igvGenomeOptions = ref<Array<{ value: string; text: string }>>([
  { value: 'hg19', text: 'Human (hg19)' },
  { value: 'hg38', text: 'Human (hg38)' },
  { value: 'mm10', text: 'Mouse (mm10)' },
  { value: 'rn6', text: 'Rat (rn6)' },
  { value: 'dm6', text: 'Drosophila (dm6)' },
  { value: 'ce11', text: 'C. elegans (ce11)' },
  { value: 'sacCer3', text: 'Yeast (sacCer3)' },
  { value: 'custom', text: 'Custom (provide URL in IGV)' },
]);
const remoteIgvUrlSelected = computed(() => igvUrl.value === igvRemoteUrl);
const localIgvUrlSelected = computed(() => igvUrl.value === igvLocalUrl);
const showIgvDesktopInstructions = ref(false);
const showIgvAdditionalUrls = computed(
  () => remoteIgvUrlSelected.value && igvAdditionalUrls.value.length > 0
);
const igvAdditionalUrls = ref<Array<{ value: string; text: string }>>([]);

// --- Component emits ---
const emits = defineEmits(['close']);

const show = computed<boolean>(() => {
  return props.show;
});

const sizeIsFitForPreview = computed(() => {
  if (!props.dataObject.size) {
    return true;
  }
  return props.dataObject.size.length < 8;
});
const isTextFile = computed(() => {
  return (
    props.dataObject.contentType?.toLowerCase().startsWith('text/') ||
    props.dataObject.name?.toLowerCase().endsWith('.html') ||
    props.dataObject.name?.toLowerCase().endsWith('.css') ||
    props.dataObject.name?.toLowerCase().endsWith('.js') ||
    props.dataObject.name?.toLowerCase().endsWith('.json') ||
    props.dataObject.name?.toLowerCase().endsWith('.xml') ||
    props.dataObject.name?.toLowerCase().endsWith('.yml') ||
    props.dataObject.name?.toLowerCase().endsWith('.yaml') ||
    props.dataObject.name?.toLowerCase().endsWith('.md') ||
    props.dataObject.name?.toLowerCase().endsWith('.txt') ||
    props.dataObject.name?.toLowerCase().endsWith('.csv') ||
    props.dataObject.name?.toLowerCase().endsWith('.tsv') ||
    props.dataObject.name?.toLowerCase().endsWith('.log') ||
    props.dataObject.name?.toLowerCase().endsWith('.sh') ||
    props.dataObject.name?.toLowerCase().endsWith('.bat') ||
    props.dataObject.name?.toLowerCase().endsWith('.ps1') ||
    props.dataObject.name?.toLowerCase().endsWith('.py') ||
    props.dataObject.name?.toLowerCase().endsWith('.graphql') ||
    props.dataObject.name?.toLowerCase().endsWith('.gql') ||
    props.dataObject.name?.toLowerCase().endsWith('.sql') ||
    props.dataObject.name?.toLowerCase().endsWith('.ts') ||
    props.dataObject.name?.toLowerCase().endsWith('.rb') ||
    props.dataObject.name?.toLowerCase().endsWith('.php') ||
    props.dataObject.name?.toLowerCase().endsWith('.java') ||
    props.dataObject.name?.toLowerCase().endsWith('.c') ||
    props.dataObject.name?.toLowerCase().endsWith('.cpp')
  );
});
const canPreviewInNewTab = computed(() => {
  return (
    props.allowDownload &&
    (isHtmlFile.value || isImageFile.value || isPdf.value)
  );
});
const isHtmlFile = computed(() => {
  if (props.dataObject && props.dataObject.name) {
    const name = props.dataObject.name.toLowerCase();
    const type = props.dataObject.contentType
      ? props.dataObject.contentType.toLowerCase()
      : '';
    return name.endsWith('.html') || type.includes('text/html');
  }
  return false;
});
const isImageFile = computed(() => {
  return (
    props.dataObject.contentType?.toLowerCase().startsWith('image/') ||
    props.dataObject.name?.toLowerCase().endsWith('.png') ||
    props.dataObject.name?.toLowerCase().endsWith('.jpg') ||
    props.dataObject.name?.toLowerCase().endsWith('.jpeg') ||
    props.dataObject.name?.toLowerCase().endsWith('.bmp') ||
    props.dataObject.name?.toLowerCase().endsWith('.svg')
  );
});
const isIgv = computed(
  () =>
    props.dataObject.name?.toLowerCase().endsWith('.bam') ||
    props.dataObject.name?.toLowerCase().endsWith('.vcf') ||
    props.dataObject.name?.toLowerCase().endsWith('.vcf.gz')
);
const isBam = computed(() =>
  props.dataObject.name?.toLowerCase().endsWith('.bam')
);
const isVcf = computed(
  () =>
    props.dataObject.name?.toLowerCase().endsWith('.vcf') ||
    props.dataObject.name?.toLowerCase().endsWith('.vcf.gz')
);
const isPdf = computed(() =>
  props.dataObject.name?.toLowerCase().endsWith('.pdf')
);
const isCsv = computed(() =>
  props.dataObject.name?.toLowerCase().endsWith('.csv')
);
const isTsv = computed(() =>
  props.dataObject.name?.toLowerCase().endsWith('.tsv')
);
const isJson = computed(() =>
  props.dataObject.name?.toLowerCase().endsWith('.json')
);
const isPython = computed(() =>
  props.dataObject.name?.toLowerCase().endsWith('.py')
);
const isLog = computed(() =>
  props.dataObject.name?.toLowerCase().endsWith('.log')
);
const isShell = computed(() =>
  props.dataObject.name?.toLowerCase().endsWith('.sh')
);
const previewEmbedded = computed(
  () =>
    props.allowDownload &&
    sizeIsFitForPreview.value &&
    !isHtmlFile.value &&
    (isTextFile.value || isImageFile.value)
);
const isDelimitedFile = computed(() => isCsv.value || isTsv.value);

// Formatted json
const formattedJson = computed(() => {
  try {
    // If fileContent is a string, parse it. If it’s already an object, use it directly.
    const obj =
      typeof fileContent.value === 'string'
        ? JSON.parse(fileContent.value)
        : fileContent.value;
    return obj; // JSON.stringify(obj, null, 2);
  } catch (e) {
    // If parsing fails, return the original content.
    return fileContent.value;
  }
});

// Parsed CSV/TSV state
const parsedFields = ref<any[]>([]);
const parsedItems = ref<any[]>([]);

const paginatedItems = computed(() => {
  const startIndex = (pagingPageCurrent.value - 1) * pagingItemsPerPage.value;
  const endIndex = pagingPageCurrent.value * pagingItemsPerPage.value;
  return parsedItems.value.slice(startIndex, endIndex);
});

const pagingPageCurrent = ref(1);
const pagingItemsPerPage = ref(PAGING_ITEMS_PER_PAGE);
const pagingTotalRows = computed(() => {
  return parsedItems.value.length || 0;
});

// --- Component methods ---
const fileContent = ref<string>('');
const loadingContent = ref<boolean>(false);
const errorContent = ref<string | null>(null);

onMounted(async () => {
  // Check if the file preview is presentable (text or image)
  if (props.dataObject.name && props.allowDownload) {
    loadingContent.value = true;
    try {
      // Get download URL
      downloadUrl.value = await getFileDownloadUrl(
        props.bucketId,
        props.dataObject.name
      );
      // validate URL
      if (!downloadUrl.value) {
        throw new Error(
          'Failed to obtain the file for download. The file might not be available anymore.'
        );
      }
      // Fetch content if preview is embedded
      if (previewEmbedded.value) {
        const response = await fetch(downloadUrl.value);
        if (!response.ok) {
          throw new Error('Failed to fetch file content.');
        }
        // Plain text content
        fileContent.value = await response.text();
        // If it's a CSV or TSV file, parse the content
        if (isDelimitedFile.value) {
          const parseResult = Papa.parse(fileContent.value, {
            header: true,
            delimiter: isCsv.value ? ',' : '\t',
            skipEmptyLines: true,
          });
          // PapaParse returns data and meta.headers
          parsedFields.value = (parseResult.meta.fields || []).map(
            (header: string) => ({
              key: header,
              label: header,
            })
          );
          parsedItems.value = parseResult.data;
        }
      }
    } catch (err: any) {
      console.error(err.message);
      errorContent.value = err.message;
    } finally {
      loadingContent.value = false;
    }
  }
});

async function onIgvRequest() {
  // validate
  if (!isIgv.value || !props.dataObject.name) {
    console.error('Not a BAM or VCF file, or missing file name');
    return;
  }
  // TODO: request new short-lived download URL (valid 1-4 hours, specify purpose as "igv")
  // get path from props.dataObject.name (e.g. folder1/folder2/ from the folder1/folder2/file.bam)
  const filePath = props.dataObject.name.split('/').slice(0, -1).join('/');

  // get file name without extenstion and path
  const baseFileName = props.dataObject.name
    .split('/')
    .pop()
    ?.split('.')
    .slice(0, -1)
    .join('.');

  // set names of BAM, BAI, VCF, TBI files
  const fileBam = `${filePath}/${baseFileName}.bam`;
  const fileVcf = `${filePath}/${baseFileName}.vcf`;
  const fileBai = `${filePath}/${baseFileName}.bam.bai`;

  // check file exists in the same bucket
  const exp_mins = 60; // 1 hour
  const q_params = { alt: 'media' };
  const purpose = localIgvUrlSelected.value ? 'igv-desktop' : 'igv-web';

  const urlBam = await getFileDownloadUrl(
    props.bucketId,
    fileBam,
    exp_mins,
    q_params,
    purpose
  );
  const urlVcf = await getFileDownloadUrl(
    props.bucketId,
    fileVcf,
    exp_mins,
    q_params,
    purpose
  );
  const urlBai = await getFileDownloadUrl(
    props.bucketId,
    fileBai,
    exp_mins,
    q_params,
    purpose
  );

  // check mandatory files are available
  if (!urlBam || !urlBai) {
    console.error('No BAM/BAI file available');
    alert(
      'No BAM/BAI file available. Please contact technical support for assistance.'
    );
    return;
  }
  // check optional files are available
  if (!urlVcf) {
    console.warn(
      'No VCF file available. You can still proceed with BAM/BAI only.'
    );
  }

  // Configure IGV URL and Search Params
  const requestBam = new URL(igvUrl.value);
  requestBam.searchParams.set('genome', igvGenome.value); // Genome is required
  requestBam.searchParams.set('file', urlBam); // BAM file is required
  requestBam.searchParams.set('index', urlBai); // BAI index is required
  if (igvLocus.value) requestBam.searchParams.set('locus', igvLocus.value); // Locus is optional

  // IGV Desktop selected
  if (localIgvUrlSelected.value) {
    // First Request for BAM (always)
    callIgvDesktopSilently(requestBam, 'igv-bam');

    // Second Request for VCF (if IGV Desktop & VCF available, file and index only)
    if (urlVcf) {
      const requestVcf = new URL(igvUrl.value);
      requestVcf.searchParams.set('file', urlVcf); // VCF file is required
      callIgvDesktopSilently(requestVcf, 'igv-vcf');
    }
  }

  // Additional info if IGV Web selected
  if (remoteIgvUrlSelected.value) {
    // IGV WEB: open BAM with index only
    window.open(requestBam.toString(), '_blank');
    // Instructions for additional tracks
    if (urlVcf) {
      igvAdditionalUrls.value.push({ value: urlVcf, text: 'VCF' });
    }
  }
}

function callIgvDesktopPrimary(url: URL, iFrameId: string) {
  // create/reuse a hidden iframe
  let iframe = document.getElementById(iFrameId) as HTMLIFrameElement | null;
  if (!iframe) {
    iframe = document.createElement('iframe');
    iframe.id = iFrameId;
    Object.assign(iframe.style, {
      width: '0px',
      height: '0px',
      border: '0',
      position: 'absolute',
      left: '-9999px',
      top: '-9999px',
    });
    document.body.appendChild(iframe);
  }
  iframe.src = url.toString();
}

function callIgvDesktopFallback(url: URL) {
  // create a hidden image to call the URL
  const img = new Image();
  img.width = 1;
  img.height = 1;
  img.style.position = 'absolute';
  img.style.left = '-9999px';
  img.src = url.toString(); // fires the GET without opening a tab

  // optional cleanup
  setTimeout(() => {
    if (img.parentNode) img.parentNode.removeChild(img);
  }, 5000);

  document.body.appendChild(img);
}

function callIgvDesktopSilently(url: URL, id: string) {
  try {
    // Try iframe first (fast + quiet)
    callIgvDesktopPrimary(url, id);
  } catch {
    // Fallback to image beacon if iframe is blocked as mixed content
    console.warn('IGV Desktop iframe blocked, trying fallback method');
    callIgvDesktopFallback(url);
  }
  // As last resort (if both are blocked), you *could* fallback to window.open(url),
  // but only after showing a small prompt explaining why.
}

function handleCloseModal() {
  emits('close');
}
</script>
<template>
  <div>
    <!-- MODAL: Storage Object Rename -->
    <b-modal
      v-model="show"
      @hide="handleCloseModal"
      :class="{ 'modal-xl': previewEmbedded, 'modal-lg': isIgv }"
    >
      <!-- Title -->
      <template #title>
        <font-awesome-icon icon="file-lines" class="me-2" />&nbsp;{{
          formatFileName(dataObject.name)
        }}
      </template>
      <b-alert v-model="errorContent" variant="danger">
        {{ errorContent }}
      </b-alert>
      <!-- Content -->
      <div v-if="dataObject" class="d-flex flex-column gap-3">
        <div
          v-if="
            dataObject.size || dataObject.contentType || dataObject.updatedAt
          "
        >
          <p v-if="dataObject.size" class="mb-0">
            <strong>Size:</strong> {{ formatFileSize(dataObject.size) }}
          </p>
          <p v-if="dataObject.contentType" class="mb-0">
            <strong>Content Type:</strong> {{ dataObject.contentType }}
          </p>
          <p v-if="dataObject.updatedAt" class="mb-0">
            <strong>Updated:</strong> {{ formatDateTime(dataObject.updatedAt) }}
          </p>
        </div>
        <div
          v-if="loadingContent"
          class="text-center"
          style="
            min-height: 200px;
            display: flex;
            align-items: center;
            justify-content: center;
          "
        >
          <b-spinner label="Loading content..." />
        </div>
        <!-- Download Url Ready -->
        <div v-else-if="downloadUrl" class="d-flex flex-column gap-3">
          <!-- BAM Files: Open link in IGV -->
          <template v-if="isIgv">
            <p class="mb-0">
              You may open this file in the
              <strong>IGV (Integrative Genomics Viewer)</strong> viewer. IGV is
              not operated by Sampling Human Inc. Your data will be provided
              directly from your cloud storage via a temporary link.
            </p>
            <!-- IGV: select URL option -->
            <b-form-group class="mb-0">
              <label for="igv-url-select" class="form-label">
                IGV Instance
                <font-awesome-icon
                  :icon="['fas', 'question-circle']"
                  class="ms-2 text-secondary"
                  id="igv-url-help-icon"
                />
                <b-tooltip target="igv-url-help-icon"
                  >Label to help you identify the reconstruction run later.
                </b-tooltip>
              </label>
              <BFormSelect
                id="igv-url-select"
                v-model="igvUrl"
                :options="igvUrlOptions"
                required
              />
            </b-form-group>
            <!-- IGV Web App: Disclaimer -->
            <b-alert
              v-model="remoteIgvUrlSelected"
              variant="danger"
              class="mb-0"
            >
              <p class="fw-bold">
                Proceed only if sharing this dataset with a third party complies
                with your legal/regulatory obligations and institutional
                policies. Do not use this option for PHI or identifiable data
                unless you (or your organization) have an appropriate agreement
                with the IGV operator permitting such use.
              </p>
              <p>
                By continuing, you accept that Sampling Human Inc. is not
                responsible for IGV or any handling of your data by that
                service.
              </p>
            </b-alert>
            <!-- IGV Desktop: Request Instructions Button with Link style only -->
            <div
              v-if="localIgvUrlSelected && !showIgvDesktopInstructions"
              class="mt-0"
            >
              <b-button
                variant="link"
                class="p-0 mb-0 text-decoration-none text-secondary"
                @click="showIgvDesktopInstructions = true"
              >
                <font-awesome-icon icon="info-circle" class="me-1" />
                Show IGV Desktop Instructions & Troubleshooting Tips
              </b-button>
            </div>
            <!-- IGV Desktop: Instructions -->
            <b-alert
              v-if="localIgvUrlSelected && showIgvDesktopInstructions"
              v-model="showIgvDesktopInstructions"
              variant="info"
              class="mb-0"
              dismissible
            >
              <h5 class="mb-3">
                IGV Desktop Instructions & Troubleshooting Tips
              </h5>
              <ol>
                <li>
                  <strong>Install IGV Desktop</strong><br />
                  Download and install IGV for your OS from the official
                  <a
                    href="https://igv.org/doc/desktop/#DownloadPage/"
                    target="_blank"
                    >IGV Desktop downloads page.</a
                  >
                </li>
                <li>
                  <strong>Enable the local control port</strong><br />
                  Open <strong>IGV</strong> → <strong>View</strong> →
                  <strong>Preferences</strong> → <strong>Advanced</strong> →
                  check <strong>Enable port</strong> and leave it at
                  <strong>60151</strong> (default).
                </li>
                <li>
                  <strong>Keep IGV running Leave IGV Desktop open</strong><br />
                  When you click
                  <b-badge variant="primary"
                    ><font-awesome-icon icon="eye" class="me-1" />Proceed to
                    IGV</b-badge
                  >
                  here, we'll send a command to
                  <code>http://127.0.0.1:60151</code> to load your data. If IGV
                  isn't running or the port isn't enabled, nothing will happen.
                </li>
                <li>
                  <strong> IGV will fetch your data directly over HTTPS</strong
                  ><br />
                  Matching index files (e.g. BAM+BAI, VCF+TBI) will be
                  automatically provided if they exist.
                </li>
              </ol>
            </b-alert>
            <!-- IGV: no PHI confirmation if IGV web selected -->
            <b-form-checkbox
              v-if="remoteIgvUrlSelected"
              v-model="igvNoPhiConfirmed"
              class="mb-0"
            >
              I confirm this dataset is de-identified / not PHI.
            </b-form-checkbox>
            <!-- IGV: genome -->
            <b-form-group label="Genome" label-for="genome-select" class="mb-0">
              <BFormSelect
                id="genome-select"
                v-model="igvGenome"
                :options="igvGenomeOptions"
                required
              />
            </b-form-group>
            <!-- IGV: locus -->
            <b-form-group
              label="Locus (optional)"
              label-for="locus-select"
              class="mb-0"
            >
              <BFormInput
                id="locus-select"
                v-model="igvLocus"
                placeholder="Gene name like BRCA1, coordinates like chr1:1-1000000, or all"
                required
              />
            </b-form-group>
            <!-- IGV Button: Open IGV -->
            <b-button
              @click="onIgvRequest"
              :disabled="!igvNoPhiConfirmed && igvUrl === igvRemoteUrl"
              :variant="igvUrl === igvRemoteUrl ? 'danger' : 'primary'"
            >
              <font-awesome-icon icon="eye" class="me-1" />
              Proceed to IGV
            </b-button>
            <!-- IGV Web: Additional Urls -->
            <b-alert
              v-model="showIgvAdditionalUrls"
              variant="info"
              class="mb-0"
            >
              <h5 class="mb-3">
                Instructions for loading additional tracks to IGV Web
              </h5>
              <p>
                Only BAM and BAI files can be loaded automatically. Additional
                tracks need to be added manually in the
                <strong>IGV Web UI</strong> → <strong>Tracks</strong> →
                <strong>URL</strong>. You may add the following tracks:
              </p>
              <ol>
                <li v-for="url in igvAdditionalUrls" :key="url.value">
                  <strong>{{ url.text }}:</strong> <code>{{ url.value }}</code>
                </li>
              </ol>
            </b-alert>
          </template>
          <!-- Button Link: Download / Preview in new tab -->
          <b-button :href="downloadUrl" target="_blank" variant="primary">
            <template v-if="canPreviewInNewTab">
              <font-awesome-icon icon="eye" class="me-1" />
              Preview in new tab
            </template>
            <template v-else>
              <font-awesome-icon icon="download" class="me-1" />
              Download
            </template>
          </b-button>
          <!-- Render Preview -->
          <div v-if="previewEmbedded">
            <!-- Render image files -->
            <div v-if="isImageFile && downloadUrl">
              <img
                :src="downloadUrl"
                alt="Image content"
                style="max-width: 100%; max-height: 80vh"
              />
            </div>
            <!-- Render tables (CSV, TSV) -->
            <div v-else-if="isDelimitedFile">
              <!-- Paging (below) -->
              <BPagination
                v-model="pagingPageCurrent"
                :total-rows="pagingTotalRows"
                :per-page="pagingItemsPerPage"
                pills
                size="sm"
                first-number
                last-number
              />
              <BForm
                class="d-flex flex-row align-items-end flex-wrap mt-1"
                @submit.prevent
              >
                <BFormGroup
                  label="Current page"
                  label-for="current-page"
                  label-size="sm"
                  class="me-3 mt-1"
                >
                  <BFormInput
                    id="current-page"
                    name="current-page"
                    v-model.number="pagingPageCurrent"
                    type="number"
                    size="sm"
                  />
                </BFormGroup>
                <BFormGroup
                  label="Items per page"
                  label-for="items-per-page"
                  label-size="sm"
                  class="me-3 mt-1"
                >
                  <BFormInput
                    id="items-per-page"
                    name="items-per-page"
                    v-model.number="pagingItemsPerPage"
                    size="sm"
                  />
                </BFormGroup>
              </BForm>

              <!-- Render table -->
              <div class="table-scroll-x">
                <b-table
                  :items="paginatedItems"
                  :fields="parsedFields"
                  striped
                  small
                  hover
                  responsive
                  class="browser-table mt-4"
                />
              </div>
            </div>
            <!-- Render JSON -->
            <pre
              v-else-if="isJson"
              class="code-highlight"
              v-html="highlightJson(formattedJson)"
            ></pre>
            <!-- Render PYTHON -->
            <pre
              v-else-if="isPython"
              class="code-highlight"
              v-html="highlightPython(fileContent)"
            ></pre>
            <!-- Render LOG -->
            <pre
              v-else-if="isLog"
              class="code-highlight"
              v-html="highlightLog(fileContent)"
            ></pre>
            <!-- Render SHELL -->
            <pre
              v-else-if="isShell"
              class="code-highlight"
              v-html="highlightShell(fileContent)"
            ></pre>
            <!-- Render text files -->
            <div v-else class="code-highlight">
              {{ fileContent }}
            </div>
          </div>
        </div>
      </div>
      <!-- Footer -->
      <template #footer>
        <b-button variant="secondary" @click="handleCloseModal">
          Close
        </b-button>
      </template>
    </b-modal>
  </div>
</template>
<style scoped>
.table-scroll-x {
  max-width: 100%; /* restrict to modal body width          */
  overflow-x: auto; /* ⬅︎ horizontal scrollbar appears as needed */
}
.table-scroll-x table {
  white-space: nowrap; /* keep cells on one line so they can scroll */
}
.language-plaintext {
  white-space: pre-wrap; /* Preserves whitespace and wraps lines */
  padding: 5px;
  background-color: #f5f5f5; /* Light background */
  border: 1px solid #ccc; /* Gray solid border */
  font-family: Menlo, Monaco, 'Courier New', monospace; /* Monospace font */
  border-radius: 4px;
  font-size: 0.9rem;
  overflow-x: auto; /* Enables horizontal scrolling if needed */
  line-height: 1.4;
}
</style>
